// COMMUNITY domain: the Gallery (work people made with flows) and the flow Marketplace.
// A listing is a sellable snapshot of a flow's graph — node chain, models, required providers —
// never its secrets or assets. Buyers get a fresh, editable copy that runs on their own connections.
import { db, byId, eq, sortBy, insert, update, ORG_ID, CURRENT_USER_ID } from './core';
import { createFlowFromChain, flowGraph } from './flows';
import { now } from '../lib/format';

export const PLATFORM_FEE = 0.15;
export const CATEGORIES = ['Characters', 'Social', 'Video', 'Product', 'Web', 'Emoji & Stickers'];
export const LICENSES: any = {
  Personal: 'Personal and internal projects',
  Commercial: 'Client and commercial work',
  Extended: 'Commercial work plus use inside products you sell',
};
export const LISTING_STATUS_LABEL: any = { published: 'Live', in_review: 'In review', draft: 'Draft', rejected: 'Rejected', unlisted: 'Unlisted' };

// Same per-node cost table as the seed generator (backend: model_pricing).
const COST: any = { 'text.prompt': 0.003, 'text.generate': 0.006, 'image.generate': 0.05, 'video.generate': 0.45, 'image.upscale': 0.012, 'image.remove_bg': 0.005, 'qc.image': 0.004, 'qc.video': 0.02, 'qc.consistency': 0.004 };

export const getCreator = (id?: any) => byId('creators', id) || {
  id, name:'Member', handle:id ? 'user-' + String(id).slice(0, 8) : 'unknown',
  followers:0, verified:false, bio:'', organization_id:ORG_ID,
};
export const myCreator = () => getCreator(CURRENT_USER_ID);
export const isOwnCreator = (c?: any) => c?.organization_id === ORG_ID;
export const getGalleryItem = (id?: any) => byId('gallery_items', id);
export const getListing = (id?: any) => byId('listings', id);
export const isOwnListing = (l?: any) => l?.organization_id === ORG_ID;
export const isForSale = (l?: any) => l?.status === 'published';
export const nodeTypeByKey = (key?: any) => db.node_types.find((t?: any) => t.key === key) || null;
export const listingForFlow = (flowId?: any) => db.listings.find((l?: any) => l.source_flow_id === flowId && l.organization_id === ORG_ID) || null;

// ── Gallery ──────────────────────────────────────────────
const ageDays = (iso?: any) => Math.max(0, (now().getTime() - Date.parse(iso)) / 86400000);
const trendScore = (it?: any) => (it.likes + it.views / 25 + it.remixes * 8) / Math.pow(ageDays(it.created_at) + 2, 0.8);

export function listGallery({ q, kind, sort = 'trending', creatorId, forSale, likedIds }: any = {}) {
  let rows = [...db.gallery_items];
  if (kind) rows = rows.filter((i?: any) => i.kind === kind);
  if (creatorId) rows = rows.filter((i?: any) => i.creator_id === creatorId);
  if (forSale) rows = rows.filter((i?: any) => isForSale(byId('listings', i.listing_id)));
  if (likedIds) rows = rows.filter((i?: any) => likedIds.includes(i.id));
  if (q) {
    const s = q.toLowerCase();
    rows = rows.filter((i?: any) => { const c = getCreator(i.creator_id); return [i.title, i.prompt, c?.name, c?.handle, ...(i.tags || [])].join(' ').toLowerCase().includes(s); });
  }
  if (sort === 'newest') return sortBy(rows, 'created_at');
  if (sort === 'liked') return sortBy(rows, 'likes');
  return sortBy(rows, trendScore);
}

export const listingWorks = (listingId?: any) => sortBy(eq('gallery_items', 'listing_id', listingId), 'likes');
export const creatorWorks = (creatorId?: any, exceptId?: any) => sortBy(db.gallery_items.filter((i?: any) => i.creator_id === creatorId && i.id !== exceptId), 'likes');
export const bumpLikes = (id?: any, delta?: any) => { const it = byId('gallery_items', id); if (it) it.likes = Math.max(0, it.likes + delta); };

export function publishWork({ assetId, title, listingId = null }: any) {
  const a = byId('assets', assetId);
  const l = listingId ? byId('listings', listingId) : null;
  const kind = a.type === 'VIDEO' ? 'video' : a.type === 'CHARACTER' ? 'character' : 'image';
  const id = 'gal_new_' + Date.now();
  insert('gallery_items', {
    id, creator_id: myCreator().id, title: title || a.name, kind, aspect: kind === 'video' ? '16 / 10' : kind === 'character' ? '1 / 1' : '4 / 5',
    thumbnail: a.thumbnail, listing_id: l?.id || null, flow_id: a.metadata?.lineage?.flow_id || l?.source_flow_id || null, asset_id: a.id,
    prompt: a.definition?.text || a.description || '', models: l?.models || [], likes: 0, views: 0, remixes: 0, featured: false, tags: [kind],
    created_at: new Date().toISOString(),
  });
  return id;
}

// ── Marketplace ──────────────────────────────────────────
export function listListings({ q, category, price, sort = 'popular', sellerId, status = 'published' }: any = {}) {
  let rows = db.listings.filter((l?: any) => !status || l.status === status);
  if (category) rows = rows.filter((l?: any) => l.category === category);
  if (price === 'free') rows = rows.filter((l?: any) => !l.price);
  if (price === 'paid') rows = rows.filter((l?: any) => l.price > 0);
  if (sellerId) rows = rows.filter((l?: any) => l.seller_id === sellerId);
  if (q) {
    const s = q.toLowerCase();
    rows = rows.filter((l?: any) => { const c = getCreator(l.seller_id); return [l.title, l.tagline, l.category, c?.name, c?.handle, ...(l.models || [])].join(' ').toLowerCase().includes(s); });
  }
  switch (sort) {
    case 'newest': return sortBy(rows, 'created_at');
    case 'rating': return sortBy(rows, (l?: any) => l.rating ?? 0);
    case 'price_asc': return sortBy(rows, 'price', 'asc');
    case 'price_desc': return sortBy(rows, 'price');
    default: return sortBy(rows, 'sales_count');
  }
}

export const categoryCounts = () => Object.fromEntries(CATEGORIES.map((c?: any) => [c, db.listings.filter((l?: any) => l.status === 'published' && l.category === c).length]));
export const listingReviews = (listingId?: any) => sortBy(eq('reviews', 'listing_id', listingId), 'created_at');
export const creatorListings = (creatorId?: any, exceptId?: any) => sortBy(db.listings.filter((l?: any) => l.seller_id === creatorId && l.id !== exceptId && l.status === 'published'), 'sales_count');

export const myListings = () => sortBy(db.listings.filter((l?: any) => l.organization_id === ORG_ID), 'updated_at');
export const myPurchases = () => sortBy(eq('purchases', 'organization_id', ORG_ID), 'created_at').map((p?: any) => ({ ...p, listing: byId('listings', p.listing_id) }));
export const purchaseOf = (listingId?: any) => db.purchases.find((p?: any) => p.listing_id === listingId && p.organization_id === ORG_ID) || null;

export function sellerStats() {
  const own = myListings();
  const gross = own.reduce((a?: any, l?: any) => a + l.price * l.sales_count, 0);
  const rated = own.filter((l?: any) => l.rating);
  return {
    gross, payout: gross * (1 - PLATFORM_FEE), sales: own.reduce((a?: any, l?: any) => a + l.sales_count, 0),
    live: own.filter((l?: any) => l.status === 'published').length, inReview: own.filter((l?: any) => l.status === 'in_review').length,
    rating: rated.length ? rated.reduce((a?: any, l?: any) => a + l.rating, 0) / rated.length : null,
  };
}

export function marketplaceStats() {
  const sold = db.listings.filter((l?: any) => l.status === 'published' || l.status === 'unlisted');
  const gmv = sold.reduce((a?: any, l?: any) => a + l.price * l.sales_count, 0);
  return {
    gmv, fees: gmv * PLATFORM_FEE,
    inReview: db.listings.filter((l?: any) => l.status === 'in_review').length,
    live: db.listings.filter((l?: any) => l.status === 'published').length,
    sellers: new Set(db.listings.filter((l?: any) => l.status === 'published').map((l?: any) => l.seller_id)).size,
  };
}

// What a buyer would get from this flow: ordered node chain, models, required providers, cost per run.
export function flowSnapshot(flowId?: any) {
  const { nodes } = flowGraph(flowId);
  const ordered = [...nodes].sort((a?: any, b?: any) => a.position_x - b.position_x || a.position_y - b.position_y);
  const providers = [...new Set(ordered.map((n?: any) => byId('providers', n.type.provider_id)?.name).filter(Boolean))];
  return {
    chain: ordered.map((n?: any) => ({ name: n.name, type_key: n.type.key })),
    models: [...new Set(ordered.map((n?: any) => n.config.model).filter(Boolean))],
    requirements: providers.map((p?: any) => `${p} connection`),
    node_count: ordered.length,
    est_cost_per_run: +ordered.reduce((a?: any, n?: any) => a + (COST[n.type.key] || 0), 0).toFixed(3),
  };
}

export function createListing({ flowId, title, tagline, description, category, price, license, workAssetIds = [] }: any) {
  const now = new Date().toISOString();
  const id = 'lst_new_' + Date.now();
  const snap = flowSnapshot(flowId);
  const cover = workAssetIds.length ? byId('assets', workAssetIds[0]).thumbnail : { hue: [...title].reduce((h?: any, c?: any) => (h * 31 + c.charCodeAt(0)) % 360, 7), glyph: 'template' };
  insert('listings', {
    id, source_flow_id: flowId, seller_id: myCreator().id, organization_id: ORG_ID, title, tagline,
    description: description || tagline, category, price: Math.max(0, Math.round(Number(price) || 0)), currency: 'USD', license,
    status: 'in_review', cover, rating: null, reviews_count: 0, sales_count: 0, version: 1, tags: [category.toLowerCase()],
    ...snap, created_at: now, updated_at: now,
  });
  workAssetIds.forEach((assetId?: any) => { const gid = publishWork({ assetId, listingId: id }); update('gallery_items', gid, { flow_id: flowId }); });
  return id;
}

export const setListingStatus = (id?: any, status?: any) => update('listings', id, { status, updated_at: new Date().toISOString() });

export function purchaseListing(listingId: any, { projectId = null, userId }: any) {
  const l = byId('listings', listingId);
  if (Number(l.price) > 0) throw new Error('Paid purchases are unavailable until a payment provider is connected.');
  const seller = getCreator(l.seller_id);
  const now = new Date().toISOString();
  const assetId = 'ast_mkt_' + Date.now();
  insert('asset_versions', { id: assetId + '_v1', asset_id: assetId, version: 1, status: 'current', metadata: { note: `Marketplace v${l.version}` }, created_by: userId, created_at: now });
  insert('assets', {
    id: assetId, organization_id: ORG_ID, name: l.title, description: l.tagline, type: 'FLOW_TEMPLATE', subtype: 'marketplace', scope: 'ORGANIZATION',
    workspace_id: null, brand_id: null, project_id: null, status: 'Approved', provider: `Marketplace · @${seller.handle}`,
    thumbnail: { ...l.cover, glyph: 'template' }, metadata: { listing_id: l.id, license: l.license, chain: l.chain },
    tags: ['marketplace', l.category], created_by: userId, created_at: now, updated_at: now, current_version: 1, family: null,
  });
  const flowId = projectId ? createFlowFromChain(projectId, l.title, l.chain, userId, { description: `Bought on Marketplace from @${seller.handle}`, templateOriginId: assetId }) : null;
  const id = 'pur_' + Date.now();
  insert('purchases', { id, listing_id: l.id, organization_id: ORG_ID, buyer_user_id: userId, price: 0, payment_status: 'free', license: l.license, asset_id: assetId, flow_id: flowId, created_at: now });
  update('listings', l.id, { sales_count: l.sales_count + 1 });
  return { purchaseId: id, flowId, assetId };
}

export function addPurchaseToProject(purchaseId?: any, projectId?: any, userId?: any) {
  const p = byId('purchases', purchaseId);
  const l = byId('listings', p.listing_id);
  const flowId = createFlowFromChain(projectId, l.title, l.chain, userId, { description: `Bought on Marketplace from @${getCreator(l.seller_id).handle}`, templateOriginId: p.asset_id });
  update('purchases', purchaseId, { flow_id: flowId });
  return flowId;
}
